/**
 * This custom hook should allow components access to the customer form state,
 * as in, what step, show the leave dialog, etc.
 */
import { Dispatch, useReducer } from "react";
import { camel } from "change-case";
import { CustomEnum } from "../types";
import { get, isNumber } from "../utils/helpers";

export interface CustomerPageObject {
  language: string;
  loading: boolean;
  step: number;
  showConfirmLeave: boolean;
  showTermsAndConditions: boolean;
  showAdditionalInfo: boolean;
  showConfirmSendEmail: boolean;
  showConfirmSalesAdvisor: boolean;
  showSubmissionResults: boolean;
}

export interface ActionSetLanguage {
  action: "setLanguage";
  payload: string;
}

export interface ActionSetStep {
  action: "setStep";
  payload: number;
}

export interface ActionSetLoading {
  action: "toggleLoading";
  payload?: boolean;
}

export interface ActionToggleModal {
  action: "toggleModal";
  payload: {
    name: "confirmLeave" | "termsAndConditions" | "additionalInfo";
    display?: boolean;
  };
}

export type CustomerPageAction =
  | ActionSetLanguage
  | ActionSetLoading
  | ActionSetStep
  | ActionToggleModal
  | { action: string; payload?: any };

export interface CustomerPageReducer {
  (state: CustomerPageObject, action: CustomerPageAction): CustomerPageObject;
}

export type UseCustomerPage = [
  CustomerPageObject,
  Dispatch<CustomerPageAction>
];

/*  const enums aren't supported in CRA+TypeScript ¯\_(ツ)_/¯
export enum FormStep {
  Data,
  Consent,
  Thanks
} */

export const FORM_STEP: CustomEnum<number> = Object.freeze({
  DATA: 0,
  CONSENT: 1,
  SUMMARY: 2,
  THANKS: 3,
  ADVISOR: 4
});

export const DEFAULT_CUSTOMER_PAGE: CustomerPageObject = {
  language: "en",
  step: FORM_STEP.DATA,
  loading: false,
  showConfirmLeave: false,
  showTermsAndConditions: false,
  showAdditionalInfo: false,
  showConfirmSendEmail: false,
  showConfirmSalesAdvisor: false,
  showSubmissionResults: false
};

export function customerPageInit(
  initialState?: Partial<CustomerPageObject>
): CustomerPageObject {
  return {
    ...DEFAULT_CUSTOMER_PAGE,
    ...initialState
  };
}

export function customerPageReducer(
  prevState: CustomerPageObject,
  { action, payload }: CustomerPageAction
): CustomerPageObject {
  switch (action) {
    case "setLanguage":
      return {
        ...prevState,
        language: payload || "en"
      };

    case "setStep":
      if (!isNumber(payload) || !Object.values(FORM_STEP).includes(payload)) {
        throw new Error(
          `Invalid FORM_STEP given at Action.payload: ${payload}`
        );
      }
      return {
        ...prevState,
        step: payload
      };

    case "toggleLoading":
      return {
        ...prevState,
        loading: payload !== undefined ? !!payload : !prevState.loading
      };

    case "toggleModal":
      if (!payload.name) {
        throw new Error(`Invalid modal name given: ${payload.name}`);
      }
      const toggleModal = camel(`show ${payload.name}`);
      return {
        ...prevState,
        [toggleModal]:
          payload.display !== undefined
            ? !!payload.display
            : !get(prevState, toggleModal)
      };

    default:
      throw new Error(`Invalid action specified: ${action}`);
  }
}

export default function useCustomerPage(
  initialState: Partial<CustomerPageObject> = {}
) {
  return useReducer(
    customerPageReducer,
    customerPageInit(initialState),
    customerPageInit
  );
}
